import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Storefront, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Landing page for the email-link password reset flow. The backend sends an
 * email containing ``/reset-password?token=XYZ``. We just grab the token from
 * the query string, accept a new password, and post it to
 * ``/api/auth/reset-password``. The token is verified + consumed server-side.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("No reset token in the link — use the button in the email.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters");
    if (pwd !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="reset-password-page">
        <div className="bg-gradient-to-br from-[#121216] to-[#1E5FEB] text-white p-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Storefront size={17} weight="duotone" />
            </div>
            <div className="font-display font-semibold tracking-tight text-[17px]">iRepair<span className="text-white/70">.</span></div>
          </div>
          <div className="overline text-white/60 mb-2">RESET PASSWORD</div>
          <h1 className="font-display text-[26px] font-bold tracking-tight">Choose a new password</h1>
        </div>

        <div className="p-7">
          {done ? (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle size={48} weight="duotone" className="mx-auto text-[#16A34A] mb-3" />
              <div className="font-display font-semibold text-[18px] mb-1">Password updated</div>
              <p className="text-[13px] text-[#4B5563]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1.5">NEW PASSWORD</div>
                <input
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  autoFocus
                  minLength={8}
                  data-testid="reset-new-pwd"
                  className="w-full h-12 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-[14px]"
                  placeholder="At least 8 characters"
                />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1.5">CONFIRM</div>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-confirm-pwd"
                  className="w-full h-12 px-3 rounded-lg border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-[14px]"
                  placeholder="Type it again"
                />
              </label>
              <button
                type="submit"
                disabled={submitting || !token}
                data-testid="reset-submit"
                className="w-full h-12 rounded-2xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:opacity-60 text-white text-[14px] font-semibold transition-colors"
              >
                {submitting ? "SAVING…" : "UPDATE PASSWORD"}
              </button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-[12px] text-[#4B5563] hover:text-[#1E5FEB] font-medium">← Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
